import React, { useState, useEffect, useRef, useCallback } from "react";

export default function FloatingCalculator({ isShifted = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isClosed, setIsClosed] = useState(false);
  const [currentInput, setCurrentInput] = useState("0");
  const [formula, setFormula] = useState("");
  const [isResetOnNextKey, setIsResetOnNextKey] = useState(false);
  const [calcTab, setCalcTab] = useState("math");
  const [breakerMode, setBreakerMode] = useState("findTime");
  const [tripInputs, setTripInputs] = useState({
    ratingIn: "",
    lSetting: "",
    t1: "",
    testCurrent: "",
    targetTime: ""
  });
  
  // Dragging states
  const [position, setPosition] = useState({ left: null, top: null });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0, left: 0, top: 0 });
  const widgetRef = useRef(null);

  // Sync initial position when opening first time
  useEffect(() => {
    if (isOpen && !isMinimized && position.left === null && widgetRef.current) {
      // Get current viewport dimensions
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      // Default initial position (bottom right)
      const width = 320;
      const height = 445;
      const left = viewportWidth - width - 24;
      const bottomOffset = isShifted ? 168 : 96;
      const top = viewportHeight - height - bottomOffset;
      
      setPosition({ left, top });
    }
  }, [isOpen, isMinimized, isShifted, position.left]);

  // Safe manual math evaluation
  const evaluateExpression = useCallback((expr) => {
    let safeExpr = expr.replace(/x/g, "*").replace(/÷/g, "/");
    safeExpr = safeExpr.replace(/[^0-9+\-*/.]/g, "");
    
    try {
      const tokens = [];
      let numberBuffer = "";
      
      for (let i = 0; i < safeExpr.length; i++) {
        const char = safeExpr[i];
        if ("0123456789.".includes(char)) {
          numberBuffer += char;
        } else {
          if (numberBuffer) {
            tokens.push(Number(numberBuffer));
            numberBuffer = "";
          }
          if (char === "-" && (tokens.length === 0 || typeof tokens[tokens.length - 1] === "string")) {
            numberBuffer = "-";
          } else {
            tokens.push(char);
          }
        }
      }
      if (numberBuffer) {
        tokens.push(Number(numberBuffer));
      }
      
      if (tokens.length === 0) return 0;
      
      // Multiplications and Divisions
      let i = 0;
      while (i < tokens.length) {
        if (tokens[i] === "*" || tokens[i] === "/") {
          const op = tokens[i];
          const prev = tokens[i - 1];
          const next = tokens[i + 1];
          if (prev === undefined || next === undefined) throw new Error("Format error");
          const res = op === "*" ? prev * next : prev / next;
          tokens.splice(i - 1, 3, res);
          i--;
        } else {
          i++;
        }
      }
      
      // Additions and Subtractions
      i = 0;
      while (i < tokens.length) {
        if (tokens[i] === "+" || tokens[i] === "-") {
          const op = tokens[i];
          const prev = tokens[i - 1];
          const next = tokens[i + 1];
          if (prev === undefined || next === undefined) throw new Error("Format error");
          const res = op === "+" ? prev + next : prev - next;
          tokens.splice(i - 1, 3, res);
          i--;
        } else {
          i++;
        }
      }
      
      if (tokens.length !== 1 || typeof tokens[0] !== "number" || Number.isNaN(tokens[0])) {
        throw new Error("Invalid output");
      }
      
      return Math.round(tokens[0] * 1e8) / 1e8;
    } catch (err) {
      return "Error";
    }
  }, []);

  const formatWithCommas = (value) => {
    if (value === "Error" || value === "Infinity") return value;
    const parts = value.split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.join(".");
  };

  const handleKeyPress = useCallback((val) => {
    let nextInput = currentInput;
    if (isResetOnNextKey) {
      nextInput = "0";
      setIsResetOnNextKey(false);
    }

    if (val === ".") {
      if (nextInput.includes(".")) return;
      setCurrentInput(nextInput + ".");
    } else if (nextInput === "0") {
      setCurrentInput(val);
    } else {
      setCurrentInput(nextInput + val);
    }
  }, [currentInput, isResetOnNextKey]);

  const handleOperator = useCallback((op) => {
    setIsResetOnNextKey(false);
    const displayOp = op === "*" ? "x" : op === "/" ? "÷" : op;
    const lastChar = formula.trim().slice(-1);
    
    if (formula && ["+", "-", "x", "÷"].includes(lastChar) && currentInput === "0") {
      setFormula(formula.trim().slice(0, -1) + ` ${displayOp} `);
    } else {
      setFormula(formula + ` ${currentInput} ${displayOp} `);
      setCurrentInput("0");
    }
  }, [formula, currentInput]);

  const handleClear = useCallback(() => {
    setCurrentInput("0");
    setFormula("");
    setIsResetOnNextKey(false);
  }, []);

  const handleBackspace = useCallback(() => {
    if (isResetOnNextKey) {
      handleClear();
      return;
    }
    
    if (currentInput.length > 1) {
      setCurrentInput(currentInput.slice(0, -1));
    } else {
      setCurrentInput("0");
    }
  }, [currentInput, isResetOnNextKey, handleClear]);

  const handleCalculate = useCallback(() => {
    if (formula === "") return;
    const fullExpression = formula + currentInput;
    const result = evaluateExpression(fullExpression);
    
    setFormula("");
    setCurrentInput(String(result));
    setIsResetOnNextKey(true);
  }, [formula, currentInput, evaluateExpression]);


  // Keyboard integration
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen || isMinimized || isClosed) return;
      const key = e.key;
      
      if (/[0-9.]/.test(key)) {
        handleKeyPress(key);
      } else if (key === "+" || key === "-" || key === "*" || key === "/") {
        handleOperator(key);
      } else if (key === "Enter" || key === "=") {
        e.preventDefault();
        handleCalculate();
      } else if (key === "Backspace") {
        handleBackspace();
      } else if (key === "Escape" || key === "c" || key === "C") {
        handleClear();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, isMinimized, isClosed, handleKeyPress, handleOperator, handleCalculate, handleBackspace, handleClear]);


  // Dragging event handlers
  const handleStartDrag = (e) => {
    if (e.type === "mousedown" && e.button !== 0) return;
    
    const clientX = e.type === "touchstart" ? e.touches[0].clientX : e.clientX;
    const clientY = e.type === "touchstart" ? e.touches[0].clientY : e.clientY;
    
    setIsDragging(true);
    
    const rect = widgetRef.current.getBoundingClientRect();
    dragStartRef.current = {
      x: clientX,
      y: clientY,
      left: rect.left,
      top: rect.top
    };

    const handleDrag = (moveEvent) => {
      const moveX = moveEvent.type === "touchmove" ? moveEvent.touches[0].clientX : moveEvent.clientX;
      const moveY = moveEvent.type === "touchmove" ? moveEvent.touches[0].clientY : moveEvent.clientY;
      
      const dx = moveX - dragStartRef.current.x;
      const dy = moveY - dragStartRef.current.y;
      
      let newLeft = dragStartRef.current.left + dx;
      let newTop = dragStartRef.current.top + dy;
      
      // Viewport bounds checking
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const widgetWidth = 320;
      const widgetHeight = 445;
      
      if (newLeft < 0) newLeft = 0;
      if (newLeft + widgetWidth > viewportWidth) newLeft = viewportWidth - widgetWidth;
      
      if (newTop < 0) newTop = 0;
      if (newTop + widgetHeight > viewportHeight) newTop = viewportHeight - widgetHeight;
      
      setPosition({ left: newLeft, top: newTop });
    };

    const handleStopDrag = () => {
      setIsDragging(false);
      document.removeEventListener("mousemove", handleDrag);
      document.removeEventListener("mouseup", handleStopDrag);
      document.removeEventListener("touchmove", handleDrag);
      document.removeEventListener("touchend", handleStopDrag);
    };

    document.addEventListener("mousemove", handleDrag);
    document.addEventListener("mouseup", handleStopDrag);
    document.addEventListener("touchmove", handleDrag, { passive: false });
    document.addEventListener("touchend", handleStopDrag);
  };

  const getFontSize = () => {
    if (currentInput.length > 15) return "20px";
    if (currentInput.length > 9) return "26px";
    return "38px";
  };

  const toggleOpen = () => {
    if (isClosed) {
      setIsClosed(false);
    }
    setIsOpen(prev => !prev);
    setIsMinimized(false);
  };

  const handleMinimize = (e) => {
    e.stopPropagation();
    setIsMinimized(true);
    setIsOpen(false);
  };

  const handleClose = (e) => {
    e.stopPropagation();
    setIsClosed(true);
    setIsOpen(false);
    setIsMinimized(false);
    setPosition({ left: null, top: null }); // Reset pos next launch
  };

  const ratingIn = parseFloat(tripInputs.ratingIn) || 0;
  const lSetting = parseFloat(tripInputs.lSetting) || 0;
  const t1 = parseFloat(tripInputs.t1) || 0;
  const testCurrent = parseFloat(tripInputs.testCurrent) || 0;
  const targetTime = parseFloat(tripInputs.targetTime) || 0;

  // Outputs for Mode 1: findTime
  let displayTestCurrent = "—";
  let displayRatio = "—";
  let displaySeconds = "—";
  let displayMinutes = "—";

  if (ratingIn > 0 && lSetting > 0 && t1 > 0 && testCurrent > 0) {
    const I1 = lSetting * ratingIn;
    const k = t1 * 9;
    const i = testCurrent / I1;
    const tSeconds = k / (i * i);
    const tMinutes = tSeconds / 60;

    displayTestCurrent = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(testCurrent) + " A";
    displayRatio = i.toFixed(2) + "×";
    displaySeconds = tSeconds.toFixed(3) + " s";
    displayMinutes = tMinutes.toFixed(3) + " min";
  }

  // Outputs for Mode 2: findCurrent
  let displayTargetI1 = "—";
  let displayTargetK = "—";
  let displayTargetRatio = "—";
  let displayRequiredCurrent = "—";

  if (ratingIn > 0 && lSetting > 0 && t1 > 0 && targetTime > 0) {
    const I1 = lSetting * ratingIn;
    const k = t1 * 9;
    const i = Math.sqrt(k / targetTime);
    const requiredCurrent = i * I1;

    displayTargetI1 = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(I1) + " A";
    displayTargetK = k.toFixed(2);
    displayTargetRatio = i.toFixed(2) + "×";
    displayRequiredCurrent = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(requiredCurrent) + " A";
  }

  return (
    <>
      <style>{`
        .calc-launcher-btn {
          position: fixed;
          right: 24px;
          bottom: 24px;
          width: 56px;
          height: 56px;
          border-radius: 50%;
          background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
          border: 1px solid rgba(255, 255, 255, 0.2);
          box-shadow: 0 10px 25px rgba(99, 102, 241, 0.4);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          transition: transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275), opacity 0.3s ease;
          outline: none;
        }
        .calc-launcher-btn:hover {
          transform: scale(1.1);
        }
        .calc-launcher-btn.hidden {
          transform: scale(0);
          opacity: 0;
          pointer-events: none;
        }
        .calc-launcher-btn svg {
          width: 24px;
          height: 24px;
          fill: #ffffff;
        }
        .calc-popup {
          position: fixed;
          width: 320px;
          background: rgba(15, 15, 27, 0.75);
          backdrop-filter: blur(25px) saturate(160%);
          -webkit-backdrop-filter: blur(25px) saturate(160%);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-top: 1px solid rgba(255, 255, 255, 0.18);
          border-radius: 24px;
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.1);
          z-index: 10000;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          opacity: 0;
          pointer-events: none;
          transform: scale(0.9) translateY(30px);
          transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.4s ease;
          user-select: none;
        }
        .calc-popup.active {
          opacity: 1;
          pointer-events: all;
          transform: scale(1) translateY(0);
        }
        .calc-popup.dragging {
          transition: none !important;
        }
        .calc-popup-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 16px;
          background: rgba(255, 255, 255, 0.02);
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
          cursor: grab;
        }
        .calc-popup-header:active {
          cursor: grabbing;
        }
        .calc-popup-title {
          font-size: 13px;
          font-weight: 600;
          color: #94a3b8;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .calc-popup-title svg {
          width: 14px;
          height: 14px;
          fill: #94a3b8;
        }
        .calc-popup-controls {
          display: flex;
          gap: 8px;
        }
        .calc-win-btn {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          border: none;
          cursor: pointer;
          position: relative;
        }
        .calc-win-close { background-color: #ef4444; }
        .calc-win-min { background-color: #ffbd2e; }
        .calc-popup-display {
          padding: 20px;
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          justify-content: flex-end;
          min-height: 100px;
          background: rgba(0, 0, 0, 0.2);
          border-bottom: 1px solid rgba(255, 255, 255, 0.03);
        }
        .calc-formula-text {
          font-size: 13px;
          color: #94a3b8;
          min-height: 18px;
          text-align: right;
          width: 100%;
          margin-bottom: 4px;
          letter-spacing: 0.5px;
        }
        .calc-current-text {
          font-family: 'Outfit', sans-serif;
          font-weight: 600;
          color: #ffffff;
          white-space: nowrap;
          overflow-x: auto;
          width: 100%;
          text-align: right;
          scrollbar-width: none;
        }
        .calc-current-text::-webkit-scrollbar { display: none; }
        .calc-popup-keypad {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 8px;
          padding: 16px;
        }
        .calc-btn {
          height: 48px;
          border-radius: 12px;
          border: 1px solid rgba(255, 255, 255, 0.02);
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
          outline: none;
          color: #f1f5f9;
          background: rgba(255, 255, 255, 0.04);
        }
        .calc-btn:hover {
          background: rgba(255, 255, 255, 0.09);
          transform: translateY(-1px);
        }
        .calc-btn:active {
          transform: scale(0.96) translateY(0);
        }
        .calc-btn-op {
          background: rgba(99, 102, 241, 0.12);
          color: #c7d2fe;
          border: 1px solid rgba(99, 102, 241, 0.15);
        }
        .calc-btn-op:hover {
          background: rgba(99, 102, 241, 0.22);
          border-color: rgba(99, 102, 241, 0.3);
        }
        .calc-btn-clear {
          background: rgba(239, 68, 68, 0.1);
          color: #fca5a5;
          border: 1px solid rgba(239, 68, 68, 0.15);
        }
        .calc-btn-clear:hover {
          background: rgba(239, 68, 68, 0.2);
        }
        .calc-btn-back {
          color: #94a3b8;
        }
        .calc-btn-back svg {
          width: 18px;
          height: 18px;
          fill: currentColor;
        }
        .calc-btn-eq {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: #ffffff;
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.25);
          border: none;
        }
        .calc-btn-eq:hover {
          background: linear-gradient(135deg, #34d399 0%, #059669 100%);
        }
        .calc-tabs {
          display: flex;
          background: rgba(255, 255, 255, 0.03);
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
        }
        .calc-tab-btn {
          flex: 1;
          background: none;
          border: none;
          color: #94a3b8;
          font-size: 11px;
          font-weight: 600;
          padding: 8px 12px;
          cursor: pointer;
          transition: all 0.2s ease;
          text-align: center;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .calc-tab-btn:hover {
          color: #f1f5f9;
          background: rgba(255, 255, 255, 0.02);
        }
        .calc-tab-btn.active {
          color: #6366f1;
          background: rgba(99, 102, 241, 0.08);
          border-bottom: 2px solid #6366f1;
        }
      `}</style>

      {/* LAUNCHER */}
      <button 
        onClick={toggleOpen}
        className={`calc-launcher-btn ${isOpen ? "hidden" : ""}`}
        style={{ 
          display: isClosed ? "none" : "flex",
          bottom: isShifted ? "96px" : "24px"
        }}
        aria-label="Open Calculator"
      >
        <svg viewBox="0 0 24 24">
          <path d="M19 2H5c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 18H5V4h14v16zM6 12h2v2H6zm0 4h2v2H6zm4-8h8v2h-8zm0 4h2v2h-2zm0 4h2v2h-2zm4-4h2v2h-2zm0 4h2v2h-2z"/>
        </svg>
      </button>

      {/* CALCULATOR WIDGET */}
      <div 
        ref={widgetRef}
        className={`calc-popup ${isOpen && !isMinimized && !isClosed ? "active" : ""} ${isDragging ? "dragging" : ""}`}
        style={{
          left: position.left !== null ? `${position.left}px` : "auto",
          top: position.top !== null ? `${position.top}px` : "auto",
          right: position.left !== null ? "auto" : "24px",
          bottom: position.left !== null ? "auto" : isShifted ? "168px" : "96px"
        }}
      >
        {/* Header (Drag Handle) */}
        <div 
          className="calc-popup-header"
          onMouseDown={handleStartDrag}
          onTouchStart={handleStartDrag}
        >
          <div className="calc-popup-title">
            <svg viewBox="0 0 24 24">
              <path d="M19 2H5c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 18H5V4h14v16zM6 12h2v2H6zm0 4h2v2H6zm4-8h8v2h-8zm0 4h2v2h-2zm0 4h2v2h-2zm4-4h2v2h-2zm0 4h2v2h-2z"/>
            </svg>
            Calculator
          </div>
          <div className="calc-popup-controls">
            <button className="calc-win-btn calc-win-min" onClick={handleMinimize} title="Minimize"></button>
            <button className="calc-win-btn calc-win-close" onClick={handleClose} title="Close"></button>
          </div>
        </div>
        <div className="calc-tabs">
          <button 
            className={`calc-tab-btn ${calcTab === "math" ? "active" : ""}`}
            onClick={() => setCalcTab("math")}
          >
            🔢 Math
          </button>
          <button 
            className={`calc-tab-btn ${calcTab === "trip" ? "active" : ""}`}
            onClick={() => setCalcTab("trip")}
          >
            ⚡ Breaker L
          </button>
        </div>

        {calcTab === "math" ? (
          <>
            {/* Display */}
            <div className="calc-popup-display">
              <div className="calc-formula-text">{formula}</div>
              <div 
                className="calc-current-text" 
                style={{ fontSize: getFontSize() }}
              >
                {formatWithCommas(currentInput)}
              </div>
            </div>

            {/* Keypad */}
            <div className="calc-popup-keypad">
              {/* Row 1 */}
              <button className="calc-btn calc-btn-clear" onClick={handleClear}>C</button>
              <button className="calc-btn calc-btn-back" onClick={handleBackspace}>
                <svg viewBox="0 0 24 24">
                  <path d="M22 3H7c-.69 0-1.23.35-1.59.88L0 12l5.41 8.11c.36.53.9.89 1.59.89h15c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H7.07L2.4 12l4.66-7H22v14zm-11.59-2L14 13.41 17.59 17 19 15.59 15.41 12 19 8.41 17.59 7 14 10.59 10.41 7 9 8.41 12.59 12 9 15.59z"/>
                </svg>
              </button>
              <button className="calc-btn calc-btn-op" onClick={() => handleOperator("/")}>÷</button>
              <button className="calc-btn calc-btn-op" onClick={() => handleOperator("*")}>×</button>

              {/* Row 2 */}
              <button className="calc-btn" onClick={() => handleKeyPress("7")}>7</button>
              <button className="calc-btn" onClick={() => handleKeyPress("8")}>8</button>
              <button className="calc-btn" onClick={() => handleKeyPress("9")}>9</button>
              <button className="calc-btn calc-btn-op" onClick={() => handleOperator("-")}>−</button>

              {/* Row 3 */}
              <button className="calc-btn" onClick={() => handleKeyPress("4")}>4</button>
              <button className="calc-btn" onClick={() => handleKeyPress("5")}>5</button>
              <button className="calc-btn" onClick={() => handleKeyPress("6")}>6</button>
              <button className="calc-btn calc-btn-op" onClick={() => handleOperator("+")}>+</button>

              {/* Row 4 */}
              <button className="calc-btn" onClick={() => handleKeyPress("1")}>1</button>
              <button className="calc-btn" onClick={() => handleKeyPress("2")}>2</button>
              <button className="calc-btn" onClick={() => handleKeyPress("3")}>3</button>
              <button className="calc-btn calc-btn-eq" style={{ gridRow: "span 2", height: "auto" }} onClick={handleCalculate}>=</button>

              {/* Row 5 */}
              <button className="calc-btn" style={{ gridColumn: "span 2" }} onClick={() => handleKeyPress("0")}>0</button>
              <button className="calc-btn" onClick={() => handleKeyPress(".")}>.</button>
            </div>
          </>
        ) : (
          <div className="calc-trip-container" style={{ padding: "16px", display: "flex", flexDirection: "column", gap: "12px", overflowY: "auto", maxHeight: "380px" }}>
            {/* Mode Switcher */}
            <div style={{ display: "flex", background: "rgba(255,255,255,0.03)", borderRadius: "8px", border: "1px solid rgba(255,255,255,0.08)", padding: "2px" }}>
              <button 
                type="button"
                className={`calc-mode-btn ${breakerMode === "findTime" ? "active" : ""}`}
                onClick={() => setBreakerMode("findTime")}
                style={{ flex: 1, background: breakerMode === "findTime" ? "#6366f1" : "none", border: "none", color: "#fff", borderRadius: "6px", fontSize: "10px", fontWeight: "700", padding: "5px 8px", cursor: "pointer", transition: "all 0.2s" }}
              >
                Find Time (s)
              </button>
              <button 
                type="button"
                className={`calc-mode-btn ${breakerMode === "findCurrent" ? "active" : ""}`}
                onClick={() => setBreakerMode("findCurrent")}
                style={{ flex: 1, background: breakerMode === "findCurrent" ? "#6366f1" : "none", border: "none", color: "#fff", borderRadius: "6px", fontSize: "10px", fontWeight: "700", padding: "5px 8px", cursor: "pointer", transition: "all 0.2s" }}
              >
                Find Current (A)
              </button>
            </div>

            {breakerMode === "findTime" ? (
              <>
                {/* Inputs Section */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                    <label style={{ fontSize: "10px", fontWeight: "700", color: "#94a3b8", textTransform: "uppercase" }}>Rating In (A)</label>
                    <input
                      type="number"
                      value={tripInputs.ratingIn}
                      onChange={(e) => setTripInputs({ ...tripInputs, ratingIn: e.target.value })}
                      placeholder="e.g. 1000"
                      style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "8px", padding: "6px 8px", fontSize: "12px", color: "#fff", outline: "none" }}
                    />
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                    <label style={{ fontSize: "10px", fontWeight: "700", color: "#94a3b8", textTransform: "uppercase" }}>L setting (x In)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={tripInputs.lSetting}
                      onChange={(e) => setTripInputs({ ...tripInputs, lSetting: e.target.value })}
                      placeholder="e.g. 0.8"
                      style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "8px", padding: "6px 8px", fontSize: "12px", color: "#fff", outline: "none" }}
                    />
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                    <label style={{ fontSize: "10px", fontWeight: "700", color: "#94a3b8", textTransform: "uppercase" }}>L t1 time (s)</label>
                    <input
                      type="number"
                      value={tripInputs.t1}
                      onChange={(e) => setTripInputs({ ...tripInputs, t1: e.target.value })}
                      placeholder="e.g. 36"
                      style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "8px", padding: "6px 8px", fontSize: "12px", color: "#fff", outline: "none" }}
                    />
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                    <label style={{ fontSize: "10px", fontWeight: "700", color: "#94a3b8", textTransform: "uppercase" }}>Test Current (A)</label>
                    <input
                      type="number"
                      value={tripInputs.testCurrent}
                      onChange={(e) => setTripInputs({ ...tripInputs, testCurrent: e.target.value })}
                      placeholder="e.g. 2400"
                      style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "8px", padding: "6px 8px", fontSize: "12px", color: "#fff", outline: "none" }}
                    />
                  </div>
                </div>

                <hr style={{ border: "0", borderTop: "1px solid rgba(255,255,255,0.08)" }} />

                {/* Outputs Section */}
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "rgba(255,255,255,0.02)", padding: "6px 10px", borderRadius: "6px", border: "1px solid rgba(255,255,255,0.03)" }}>
                    <span style={{ fontSize: "11px", color: "#94a3b8" }}>Test Current:</span>
                    <span style={{ fontSize: "12px", fontWeight: "700", color: "#6366f1" }}>{displayTestCurrent}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "rgba(255,255,255,0.02)", padding: "6px 10px", borderRadius: "6px", border: "1px solid rgba(255,255,255,0.03)" }}>
                    <span style={{ fontSize: "11px", color: "#94a3b8" }}>I / I1 ratio:</span>
                    <span style={{ fontSize: "12px", fontWeight: "700", color: "#f59e0b" }}>{displayRatio}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "rgba(255,255,255,0.02)", padding: "6px 10px", borderRadius: "6px", border: "1px solid rgba(255,255,255,0.03)" }}>
                    <span style={{ fontSize: "11px", color: "#94a3b8" }}>Trip Time (s):</span>
                    <span style={{ fontSize: "12px", fontWeight: "700", color: "#10b981" }}>{displaySeconds}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "rgba(255,255,255,0.02)", padding: "6px 10px", borderRadius: "6px", border: "1px solid rgba(255,255,255,0.03)" }}>
                    <span style={{ fontSize: "11px", color: "#94a3b8" }}>Trip Time (min):</span>
                    <span style={{ fontSize: "12px", fontWeight: "700", color: "#10b981" }}>{displayMinutes}</span>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Inputs Section */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                    <label style={{ fontSize: "10px", fontWeight: "700", color: "#94a3b8", textTransform: "uppercase" }}>Rating In (A)</label>
                    <input
                      type="number"
                      value={tripInputs.ratingIn}
                      onChange={(e) => setTripInputs({ ...tripInputs, ratingIn: e.target.value })}
                      placeholder="e.g. 1000"
                      style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "8px", padding: "6px 8px", fontSize: "12px", color: "#fff", outline: "none" }}
                    />
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                    <label style={{ fontSize: "10px", fontWeight: "700", color: "#94a3b8", textTransform: "uppercase" }}>L setting (x In)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={tripInputs.lSetting}
                      onChange={(e) => setTripInputs({ ...tripInputs, lSetting: e.target.value })}
                      placeholder="e.g. 1.0"
                      style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "8px", padding: "6px 8px", fontSize: "12px", color: "#fff", outline: "none" }}
                    />
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                    <label style={{ fontSize: "10px", fontWeight: "700", color: "#94a3b8", textTransform: "uppercase" }}>L t1 time (s)</label>
                    <input
                      type="number"
                      value={tripInputs.t1}
                      onChange={(e) => setTripInputs({ ...tripInputs, t1: e.target.value })}
                      placeholder="e.g. 12"
                      style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "8px", padding: "6px 8px", fontSize: "12px", color: "#fff", outline: "none" }}
                    />
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                    <label style={{ fontSize: "10px", fontWeight: "700", color: "#94a3b8", textTransform: "uppercase" }}>Target Time (s)</label>
                    <input
                      type="number"
                      value={tripInputs.targetTime}
                      onChange={(e) => setTripInputs({ ...tripInputs, targetTime: e.target.value })}
                      placeholder="e.g. 12"
                      style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "8px", padding: "6px 8px", fontSize: "12px", color: "#fff", outline: "none" }}
                    />
                  </div>
                </div>

                <hr style={{ border: "0", borderTop: "1px solid rgba(255,255,255,0.08)" }} />

                {/* Outputs Section */}
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "rgba(255,255,255,0.02)", padding: "6px 10px", borderRadius: "6px", border: "1px solid rgba(255,255,255,0.03)" }}>
                    <span style={{ fontSize: "11px", color: "#94a3b8" }}>I1 Threshold (A):</span>
                    <span style={{ fontSize: "12px", fontWeight: "700", color: "#e2e8f0" }}>{displayTargetI1}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "rgba(255,255,255,0.02)", padding: "6px 10px", borderRadius: "6px", border: "1px solid rgba(255,255,255,0.03)" }}>
                    <span style={{ fontSize: "11px", color: "#94a3b8" }}>k factor:</span>
                    <span style={{ fontSize: "12px", fontWeight: "700", color: "#e2e8f0" }}>{displayTargetK}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "rgba(255,255,255,0.02)", padding: "6px 10px", borderRadius: "6px", border: "1px solid rgba(255,255,255,0.03)" }}>
                    <span style={{ fontSize: "11px", color: "#94a3b8" }}>Req. Ratio (i):</span>
                    <span style={{ fontSize: "12px", fontWeight: "700", color: "#f59e0b" }}>{displayTargetRatio}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "rgba(255,255,255,0.02)", padding: "6px 10px", borderRadius: "6px", border: "1px solid rgba(255,255,255,0.03)" }}>
                    <span style={{ fontSize: "11px", color: "#94a3b8" }}>Req. Test Current:</span>
                    <span style={{ fontSize: "12px", fontWeight: "700", color: "#6366f1" }}>{displayRequiredCurrent}</span>
                  </div>
                </div>
              </>
            )}

            <button 
              type="button" 
              onClick={() => setTripInputs({ ratingIn: "", lSetting: "", t1: "", testCurrent: "", targetTime: "" })}
              style={{ marginTop: "4px", background: "rgba(239, 68, 68, 0.1)", border: "1px solid rgba(239, 68, 68, 0.15)", borderRadius: "8px", padding: "6px 12px", fontSize: "11px", color: "#fca5a5", cursor: "pointer", fontWeight: "600", transition: "all 0.2s" }}
            >
              Clear Fields
            </button>
          </div>
        )}
      </div>
    </>
  );
}
